import Head from 'next/head'
import dynamic from 'next/dynamic'
import React, { useEffect, useState,useCallback, useMemo, useContext } from 'react'
import createTheme from '../lib/react-storefront/theme/createTheme'
import { red } from '@material-ui/core/colors'

import { CssBaseline, useMediaQuery } from '@material-ui/core'
import { makeStyles, MuiThemeProvider } from '@material-ui/core/styles'
import PWA from '../components/header/PWA'
import useJssStyles from '../lib/react-storefront/hooks/useJssStyles'

import Button from '@material-ui/core/Button'
import Container from '@material-ui/core/Container'
import Grid from '@material-ui/core/Grid'
import { useRouter } from 'next/router'
import { getFirebaseApp } from '../config/firebase'
import SessionProvider from '../providers/SessionProvider'
import useAppStore from '../hooks/useAppStore'
import '../styles/main.css'
const bugsnag = require('../config/bugsnag')
const bugsnagReactPlugin = bugsnag.getPlugin('react')
const ErrorBoundary = bugsnagReactPlugin?.createErrorBoundary(React) || React.Fragment
let theme = require('../components/theme')
const Header = dynamic(() => import('../components/Header'), {
  suspense: true,
})
const Footer = dynamic(() => import('../components/Footer'), {
  suspense: true,
})
const ConfirmAge = dynamic(() => import('../components/dialogs/ConfirmAge'), {
  suspense: true,
})
import reportError from '../components/reportError'

const CentralHeader = dynamic(() => import('../components/central-header/headercentral'), {
  suspense: true,
})
import Box from '@material-ui/core/Box'
const FooterCentral = dynamic(() => import('../components/central-footer/FooterCentral'), {
  suspense: true,
})
const StoreDialog = dynamic(() => import('../components/StoreDialog'), {
  suspense: true,
})
const styles = () => ({
  main: {
    paddingTop: 3,
  },
  buttonCusBgColor: (c) => ({
    backgroundColor: c.backgroundColor,
    border: '1px solid ' + c.color,
  }),
})

const useStyles = makeStyles(styles)

export default function MyApp({ Component, pageProps }) {
  useJssStyles()
  const router = useRouter()
  let { storeSlug } = router.query
  const [open, setOpen] = React.useState(false)
  const [appData] = useAppStore(pageProps || {})
  let themeColor = appData?.config?.web_theme_color || '#1e3041'
  let fontColor = appData?.config?.web_theme_font_color || '#fff'
  const mystyle = {
    backgroundColor: `${themeColor} !important`,
    color: `${fontColor} !important`,
  }
  const is_color_dark = appData?.config?.is_color_dark || null

  const classes = useStyles(mystyle)
  theme = createTheme({
    palette: {
      primary: {
        main: storeSlug ? themeColor : '#000',
        contrastText: storeSlug ? `${fontColor} !important` : '#fff',
      },
      secondary: {
        main: '#000',
        contrastText: '#1e3041 !important',
      },
      error: {
        main: red.A400,
      },
      warning:{
        main:red.A700
      }
    },
    overrides: {
      // Name of the component
      RSFNavTab: {
        link:{
          fontWeight: '300px',
          color: fontColor ? `${fontColor}` + ' !important' : '#FFF',
        }
      },
      RSFCartButton: {
        icon: {
          color: fontColor ? `${fontColor}` + ' !important' : '#FFF',
        },
        badge: {
          color: fontColor ? `${fontColor}` + ' !important' : '#fff',
          border: '2px solid ' + `${fontColor}` + ' !important',
        },
      },

      MuiRadio: {
        root: {
          color: '#9c9c9c !important',
        },
      },
      MuiAppBar: {
        colorPrimary: {
          backgroundColor: storeSlug ? `${themeColor}` + ' !important' : '#1e3041',
        },
      },
      MuiButton: {
        textPrimary: {
          color: '#000 !important',
        },

        containedPrimary: {
          backgroundColor: storeSlug ? `${themeColor}` : '#327bbf !important',
          color: storeSlug ? (fontColor ? `${fontColor}` : '#fff !important') : '#fff !important',
        },
      },
      RSFSearchButton: {
        icon: {
          color: fontColor && fontColor != '#FFFFFF' ? `${fontColor}` : '#FFFFFF',
        },
      },

      RSFMenuIcon: {
        root: {
          '& .rsf-hamburger-inner, & .rsf-hamburger-inner::before, & .rsf-hamburger-inner::after': {
            backgroundColor: fontColor && fontColor != '#FFFFFF' ? `${fontColor}` : '#FFFFFF',
          },
        },
      },

      MuiTypography: {
        subtitle1: {
          color: '#000 !important',
        },
        colorPrimary: {
          color: fontColor && fontColor != '#FFFFFF' ? `${fontColor}` : '#9c9c9c',
        },
      },
      RSFSearchField: {
        input: {
          border: '0px solid !important',
        },
      },
    },
    components: {},
  })

  const [user, setUser] = useState(null)
  let isBottleZoo = appData && appData.config && appData.config.isBottleZoo
  let isCustomDomain = appData?.config?.isCustomDomain
  const isMedium = useMediaQuery(theme.breakpoints.down('md'))
  const is404Page = router.pathname === '/404'
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        registration.unregister()
      })
    }
    localStorage.setItem('isBottleZoo', isBottleZoo)
  }, [isBottleZoo])

  // The Firebase web API key arrives at runtime via appData.config.firebase_api_key
  // and may be absent on the first render. Re-run when tenant/key change so the
  // listener attaches to the real SDK once the key is available — a keyless
  // getFirebaseApp() returns a no-op stub whose onAuthStateChanged never fires,
  // which otherwise leaves the session loader spinning forever.
  useEffect(() => {
    if (process.env.NEXT_PUBLIC_MOCK_AUTH === 'true') {
      setUser({
        uid: 'mock-user-id',
        email: 'mock@bottlezoo.test',
        emailVerified: true,
        displayName: 'Mock User',
        getIdToken: async () => 'mock-token',
      })
      return
    }
    if (!appData?.config) return
    const tenantId = appData.config.tenant_id
    const apiKey = appData.config.firebase_api_key
    if (tenantId && apiKey) {
      const unsubscribe = getFirebaseApp(tenantId, apiKey).auth.onAuthStateChanged((firebaseUser) => {
        setUser(firebaseUser || {})
      })
      return () => {
        if (typeof unsubscribe === 'function') unsubscribe()
      }
    }
    // appData loaded but Firebase is unavailable (no tenant or missing key):
    // fall back to a logged-out state so non-auth pages still render instead of
    // hanging on the loader.
    setUser({})
  }, [appData?.config?.tenant_id, appData?.config?.firebase_api_key])
  const handleClickOpen = useCallback(() => {
    setOpen(true)
  })
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      const onBeforeUnload = event => {
        window.history.back()
      }
      window.addEventListener('beforeunload', onBeforeUnload)
      router.beforePopState(() => {
        window.location.reload()
      })
      return () => {
        window.removeEventListener('beforeunload', onBeforeUnload)
        router.beforePopState(() => true)
      }
    }
  }, [router])
  return (
    <ErrorBoundary onError={reportError}>
      <PWA>
        <Head></Head>
        <SessionProvider
          config={appData?.config}
          store={storeSlug}
          user={user}
          url="/api/session"
        >
          <MuiThemeProvider theme={theme}>
            <CssBaseline />
            <ConfirmAge></ConfirmAge>
            <div>
              {!is404Page && isBottleZoo && (
                <div className={`main-blk  ${is_color_dark ? 'nav-bg-dark' : 'nav-bg-light'}`}>
                  <CentralHeader
                    isBottleZoo={isBottleZoo}
                    menu={appData && appData.tabs}
                    appData={appData}
                    config={appData && appData.config}
                  />
                </div>
              )}

              <div className="outer-wrapper">
                {!is404Page && storeSlug && (
                  <div className="header-sec" style={{ marginTop: isBottleZoo ? '5px' : '0px' }}>
                    {!isBottleZoo && (
                      <Header
                        bottlezoo={isBottleZoo}
                        menu={appData && appData.tabs}
                        appData={appData}
                        config={appData && appData.config}
                      />
                    )}

                    {(( appData?.stores?.length > 1) || isBottleZoo) && isMedium && !isCustomDomain && (
                        <Container maxWidth="lg" className="header-top">
                          <Grid container spacing={2}>
                            <Grid item xs={12} lg={3} md={3}></Grid>
                            <Grid item xs={12} lg={6} md={6}>
                            <div className="right-info d-flex">
                              <StoreDialog
                                stores={appData && appData.stores}
                                open={open}
                                setOpen={setOpen}
                              ></StoreDialog>
                              {appData && appData.config ? (
                                <Box className="top-address">
                                  <h2>{appData.config.bizname}</h2>
                                  <Box>
                                    {' '}
                                    {appData.config.bizaddress}, {appData.config.bizsuburb},{' '}
                                    {appData.config.bizstate}, {appData.config.bizcountry},{' '}
                                    {appData.config.bizpostcode}
                                  </Box>
                                </Box>
                              ) : (
                                <></>
                              )}
                              <div>
                                <Button
                                  variant="contained"
                                  color="primary"
                                  onClick={handleClickOpen}
                                  style={{ background: themeColor }}
                                >
                                  Change
                                </Button>
                              </div>
                            </div>
                            </Grid>
                          </Grid>
                        </Container>
                    )}
                  </div>
                )}

                <main className={classes.main + ' main-container'}>               
                  <Component {...pageProps} />
                </main>

                {!is404Page && appData?.config &&
                  (storeSlug && !isBottleZoo ? (
                    <Footer config={appData.config}></Footer>
                  ) : (
                    <FooterCentral config={appData.config} storeSlug={storeSlug}></FooterCentral>
                  ))}
              </div>
            </div>
          </MuiThemeProvider>
        </SessionProvider>
      </PWA>
    </ErrorBoundary>
  )
}

MyApp.getInitialProps = async function({ Component, ctx }) {
  let pageProps = {}

  if (Component.getInitialProps) {
    pageProps = await Component.getInitialProps(ctx)
  }

  return { pageProps }
}
